import React, { useCallback, useRef, useState } from 'react';

import { toolkit } from 'services/api';
import { Flow, Question } from 'types';
import { useEventTracking } from 'hooks/useEventTracking';

import Popup, { PopupHandle } from '../popup';
import { TextOrAudioResponseQuestion } from '../questions/TextOrAudioResponseQuestion';
import { Icon } from '../icons/Icon';

interface GenerateNoteFromFlowButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  question: Question;
  flow: Flow | null | undefined;
  isSubmitting?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export const GenerateNoteFromFlowButton = React.forwardRef<
  HTMLButtonElement,
  GenerateNoteFromFlowButtonProps
>(({ question, flow, isSubmitting = false, className, onClick, type, ...buttonProps }, ref) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<PopupHandle | null>(null);

  const { track } = useEventTracking();

  const questionId = question?.id ?? null;

  const handleClosePopup = useCallback(() => {
    setNoteText('');
    setAudioBlob(null);
    setError(null);
    setIsPopupOpen(false);
  }, []);

  const handleSaveNote = useCallback(async () => {
    const trimmedText = noteText.trim();
    if (!trimmedText || !questionId) {
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        title: question?.text ?? 'Untitled note',
        body: trimmedText,
        source_flow: flow?.id ?? null,
        source_flow_name: flow?.name ?? '',
      };

      const responseData = await toolkit.createNote(payload);
      const createdNote = responseData?.data;

      if (createdNote?.id) {
        track('button_click', {
          action: 'toolkit_note_create',
          note_id: createdNote.id,
          source: 'flow',
        });
        track('toolkit_note_create', {
          note_id: createdNote.id,
          source: 'flow',
        });
      }

      popupRef.current?.close();
    } catch (err) {
      console.error('Failed to create toolkit note from flow', err);
      setError('Failed to create note. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [flow, noteText, question?.text, questionId, track]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(event);
      }

      if (event.defaultPrevented) {
        return;
      }

      setIsPopupOpen(true);
    },
    [onClick],
  );

  return (
    <>
      <button
        ref={ref}
        className={`z-[400] rounded-full w-12 h-12 rounded-full bg-white/40 backdrop-blur-sm backdrop-brightness-[110%] [-webkit-backdrop-filter:blur(4px)_brightness(100%)] flex items-center justify-center hover:bg-white transition-colors shadow-elevation-sm border border-solid border-white/10 ${className ?? ''}`}
        type={type ?? 'button'}
        onClick={handleClick}
        disabled={isSubmitting || isSaving || !questionId}
        aria-label="Create toolkit note from this question"
        data-tour-id="question-flow-control-note"
        {...buttonProps}
      >
        <Icon name="bookDark" className="min-w-7 min-h-7 text-black" />
      </button>

      {isPopupOpen && (
        <Popup
          ref={popupRef}
          onClose={handleClosePopup}
          title="Enter Note Text"
          header="CREATE NOTE"
          description="Capture anything that stood out so you can revisit it later."
          buttonText={isSaving ? 'Saving…' : 'Save Note'}
          onClickButton={handleSaveNote}
          isSubmitting={isSaving}
          isButtonDisabled={noteText.trim().length < 5}
        >
          <TextOrAudioResponseQuestion
            placeholder="Write your note here..."
            inputTextValue={noteText}
            audioBlob={audioBlob}
            onTextChange={setNoteText}
            onAudioChange={setAudioBlob}
            isSubmitting={isSaving}
          />
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </Popup>
      )}
    </>
  );
});

GenerateNoteFromFlowButton.displayName = 'GenerateNoteFromFlowButton';
